// Campo de horário do app (AC-126): segue o formato 12h/24h do perfil e tem um único
// ícone de relógio, à esquerda (o seletor nativo do sistema mostrava um segundo relógio).
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTime, parseTimeText } from '../domain/dates';
import type { TimeFormat } from '../domain/settings';
import { Icon } from './Icon';
import { cls } from './NewTaskForm';

interface Props {
  /** "HH:MM" ou "" (sem horário) */
  value: string;
  onChange(value: string): void;
  label: string;
  format: TimeFormat;
  disabled?: boolean;
  className?: string;
}

const display = (v: string, format: TimeFormat) => (v ? formatTime(v, format).replace(/ (AM|PM)$/, '') : '');
const meridiemOf = (v: string): 'AM' | 'PM' => (v && Number(v.slice(0, 2)) >= 12 ? 'PM' : 'AM');

export function TimeInput({ value, onChange, label, format, disabled, className }: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState(display(value, format));
  const [mer, setMer] = useState<'AM' | 'PM'>(meridiemOf(value));
  const [invalid, setInvalid] = useState(false);
  const focused = useRef(false);

  // valor mudou por fora (ou trocou o formato): mostra de novo, se a pessoa não estiver digitando
  useEffect(() => {
    if (focused.current) return;
    setText(display(value, format));
    setMer(meridiemOf(value));
  }, [value, format]);

  const emit = (raw: string, meridiem: 'AM' | 'PM') => {
    const parsed = raw.trim() ? parseTimeText(raw, format, meridiem) : '';
    onChange(parsed ?? '');
    return parsed;
  };

  return (
    <span className={cls('time-input', invalid && 'time-input--invalid', className)}>
      <Icon name="clock" size={13} />
      <input
        className="time-input__field"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={format === '12h' ? t('time.placeholder12') : t('time.placeholder24')}
        aria-label={label}
        aria-invalid={invalid || undefined}
        value={text}
        disabled={disabled}
        onFocus={() => (focused.current = true)}
        onChange={(e) => {
          setText(e.target.value);
          setInvalid(false);
          emit(e.target.value, mer);
        }}
        onBlur={() => {
          focused.current = false;
          const parsed = emit(text, mer);
          if (parsed) {
            setText(display(parsed, format));
            setMer(meridiemOf(parsed));
          } else setInvalid(text.trim().length > 0);
        }}
      />
      {format === '12h' && (
        <button
          type="button"
          className="time-input__mer"
          aria-label={t('time.meridiem')}
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const next = mer === 'AM' ? 'PM' : 'AM';
            setMer(next);
            emit(text, next);
          }}
        >
          {mer}
        </button>
      )}
    </span>
  );
}
